import React from "react";
import { TFunction } from "i18next";
import { Box, alpha } from "@mui/material";
import {
  ArchiveOutlined,
  AssessmentOutlined,
  BusinessOutlined,
  CampaignOutlined,
  ChatBubbleOutlineRounded,
  ConfirmationNumberOutlined,
  HelpCenterOutlined,
  LibraryBooksOutlined,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { EvaluationStatus } from "../../Enums/companyStatus";
import { AgentIcon } from "../../Helpers/toolPresentation";
import { DashboardItem, DashboardItemType } from "../../Models/dashboard";
import { useDashboardColors } from "./dashboardColors";

export const itemPath = (item: DashboardItem): string => {
  switch (item.type) {
    case "virtual_team_member":
      return `/como-experts?agent=${item.id}`;
    case "conversation":
      return `/como-experts/${item.id}`;
    case "entity":
      return `/entity/${item.id}`;
    case "evaluation":
      return `/entity/${item.meta.company_id}`;
    case "question_list":
      return `/question_list/${item.id}`;
    case "knowledge_base":
      return `/knowledge_base/edit/${item.id}`;
    case "voices_campaign":
      return `/voices/campaigns/${item.id}`;
    case "help_desk_ticket":
      return `/help-desk?ticket=${item.id}`;
  }
};

const TYPE_ICONS: Record<
  Exclude<DashboardItemType, "virtual_team_member">,
  React.ElementType
> = {
  conversation: ChatBubbleOutlineRounded,
  entity: BusinessOutlined,
  evaluation: AssessmentOutlined,
  question_list: HelpCenterOutlined,
  knowledge_base: LibraryBooksOutlined,
  voices_campaign: CampaignOutlined,
  help_desk_ticket: ConfirmationNumberOutlined,
};

// Work done with AI wears the accent; the user's own records the primary.
const ACCENT_TYPES = new Set<DashboardItemType>([
  "virtual_team_member",
  "conversation",
  "evaluation",
  "voices_campaign",
]);

export const IconTile: React.FC<{
  tone: "accent" | "primary";
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const { primary, accent, accentIcon } = useDashboardColors();
  const tint = tone === "accent" ? accent : primary;

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: tone === "accent" ? accentIcon : primary,
        backgroundColor: alpha(tint, 0.1),
        border: `1px solid ${alpha(tint, 0.22)}`,
      }}
    >
      {children}
    </Box>
  );
};

export const ItemIcon: React.FC<{
  item: Pick<DashboardItem, "type" | "title" | "meta">;
}> = ({ item }) => {
  const content =
    item.type === "virtual_team_member" ? (
      <AgentIcon name={item.title} iconUrl={item.meta.icon} size="md" />
    ) : (
      React.createElement(
        item.meta.archived ? ArchiveOutlined : TYPE_ICONS[item.type],
        { sx: { fontSize: 20 } },
      )
    );

  return (
    <IconTile tone={ACCENT_TYPES.has(item.type) ? "accent" : "primary"}>
      {content}
    </IconTile>
  );
};

export type ItemStatusTone = "neutral" | "progress" | "success" | "danger";

const IN_PROGRESS_EVALUATION = new Set<number>([
  EvaluationStatus.EVALUATION_IN_PROGRESS,
  EvaluationStatus.GATHERING_DIGITAL_PERFORMANCE,
  EvaluationStatus.FETCHING_COMPANY_DETAILS,
  EvaluationStatus.PROCESSING_COMPANY_INFORMATIONS,
  EvaluationStatus.GRADING_COMPANY,
  EvaluationStatus.INITIALIZED,
]);

/** A short status label for items where state matters, otherwise null. */
export const itemStatus = (
  item: DashboardItem,
  t: TFunction,
): { label: string; tone: ItemStatusTone } | null => {
  const status = item.meta.status;

  if (item.type === "evaluation" && status !== null && status !== undefined) {
    if (IN_PROGRESS_EVALUATION.has(status))
      return { label: t("hub.status.evaluationRunning"), tone: "progress" };
    if (status === EvaluationStatus.PAUSED)
      return { label: t("hub.status.evaluationPaused"), tone: "neutral" };
    if (status === EvaluationStatus.EVALUATED)
      return { label: t("hub.status.evaluationDone"), tone: "success" };
    if (status === EvaluationStatus.NEEDS_MORE_INFO)
      return { label: t("hub.status.evaluationNeedsInfo"), tone: "neutral" };
    return { label: t("hub.status.evaluationFailed"), tone: "danger" };
  }

  if (item.type === "help_desk_ticket") {
    if (status === 1)
      return { label: t("hub.status.ticketInProgress"), tone: "progress" };
    if (status === 2)
      return { label: t("hub.status.ticketClosed"), tone: "neutral" };
    return { label: t("hub.status.ticketSubmitted"), tone: "neutral" };
  }

  if (item.type === "question_list" && item.meta.archived)
    return { label: t("hub.status.archived"), tone: "neutral" };

  if (item.type === "voices_campaign" && status === 1)
    return { label: t("hub.status.campaignClosed"), tone: "neutral" };

  if (item.type === "entity" && item.meta.is_shared)
    return { label: t("hub.status.shared"), tone: "neutral" };

  return null;
};

export const STATUS_TONES: Record<
  ItemStatusTone,
  { color: string; background: string }
> = {
  neutral: { color: color.gray600, background: color.gray100 },
  progress: { color: color.blue700, background: color.blue50 },
  success: { color: color.green700, background: color.green50 },
  danger: { color: color.red700, background: color.red50 },
};

export const StatusPill: React.FC<{
  label: string;
  tone: ItemStatusTone;
}> = ({ label, tone }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: 0.125,
      borderRadius: "6px",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      color: STATUS_TONES[tone].color,
      backgroundColor: STATUS_TONES[tone].background,
    }}
  >
    {label}
  </Box>
);

/** Parses a naive server timestamp ("2026-06-18T17:01:56") as local time. */
export const parseServerTime = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T").slice(0, 19));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const makeTimeAgo = (t: TFunction, language: string) => {
  return (value: string | null | undefined): string | null => {
    const date = parseServerTime(value);
    if (!date) return null;

    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return t("hub.time.justNow");
    if (minutes < 60) return t("hub.time.minutesAgo", { count: minutes });

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("hub.time.hoursAgo", { count: hours });

    const days = Math.floor(hours / 24);
    if (days < 7) return t("hub.time.daysAgo", { count: days });

    return date.toLocaleDateString(language, {
      day: "numeric",
      month: "short",
      year:
        date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    });
  };
};
